import { openDatabase, saveSnapshotDatabase } from "../Utils/db";

import type { ITriggerRule, TriggerEvent } from "../interfaces/Widgets";

import { WidgetsType } from "../interfaces/Widgets";

import type Radio from "./Radio";

import type {
  TriggerHandler,
  TriggerActionType,
  TriggerUnsubscribe,
  EventSourceResult,
} from "./TriggerEngine.interfaces";

import { actionRegistry } from "./triggers";

import { matchesChatEvent } from "./triggers/chatMatch";

import { EVENT_SOURCE_MAP, type EventSourceWireFn } from "./triggers/events";

class TriggerEngine {
  Radio: Radio;

  private wiredSources: Map<EventSourceWireFn, EventSourceResult> = new Map();

  private ready = false;

  private executing = false;

  private suppressedEvents: Set<string> = new Set();

  private actionHandlers: Map<TriggerActionType, Set<TriggerHandler>> =
    new Map();

  private globalEventHandlers: Set<(event: string, payload?: unknown) => void> =
    new Set();

  constructor(radio: Radio) {
    this.Radio = radio;
  }

  async sync() {
    const rules = await this.getRules();

    const enabledEvents = new Set(
      rules.filter((r) => r.enabled).map((r) => r.event),
    );

    const neededSources = new Set<EventSourceWireFn>();

    for (const event of enabledEvents) {
      const source = EVENT_SOURCE_MAP[event];

      if (source) neededSources.add(source);
    }

    // Unwire sources that are no longer needed

    for (const [sourceFn, result] of this.wiredSources) {
      if (!neededSources.has(sourceFn)) {
        for (const unsub of result.unsubscribers) unsub();

        if (result.timers) {
          for (const timer of result.timers.values()) clearTimeout(timer);

          result.timers.clear();
        }

        this.wiredSources.delete(sourceFn);
      }
    }

    // Wire sources that are needed but not yet wired

    for (const sourceFn of neededSources) {
      if (this.wiredSources.has(sourceFn)) continue;

      const result = await sourceFn(this);

      this.wiredSources.set(sourceFn, result);
    }

    this.ready = enabledEvents.size > 0;
  }

  destroy() {
    for (const [, result] of this.wiredSources) {
      for (const unsub of result.unsubscribers) unsub();

      if (result.timers) {
        for (const timer of result.timers.values()) clearTimeout(timer);

        result.timers.clear();
      }
    }

    this.wiredSources.clear();

    this.ready = false;

    this.executing = false;

    this.suppressedEvents.clear();

    this.actionHandlers.clear();

    this.globalEventHandlers.clear();
  }

  /**
   * Subscribe to ALL events fired through `fireEvent`. The handler is invoked
   * once per fired event (after the suppression check) regardless of whether
   * any rule matches. Returns an unsubscribe function.
   */

  subscribeAll(
    handler: (event: string, payload?: unknown) => void,
  ): TriggerUnsubscribe {
    this.globalEventHandlers.add(handler);

    return () => {
      this.globalEventHandlers.delete(handler);
    };
  }

  /**
   * Dispatch an event coming from outside the Studio (e.g. the SSE
   * EventBus surfacing platform events like `event_follow`). Wraps
   * `fireEvent` so external callers don't depend on its exact signature
   * (payload is `Record<string, string>` for rule-matching purposes).
   *
   * The full object payload is forwarded as-is — rule-matching only
   * inspects string fields, so structured data is silently ignored by
   * the matcher but still reaches global subscribers via
   * `subscribeAll`. Errors are swallowed so external dispatchers never
   * crash on rule-execution failures.
   */

  dispatchExternal(eventName: TriggerEvent, payload?: unknown): void {
    // Cast: fireEvent's payload type is too narrow for the rich

    // StreamEvent/ChatMessage objects we route through here. The engine

    // tolerates non-string fields (only string keys are checked against

    // rule.eventConfig), so the cast is safe in practice.

    void this.fireEvent(
      eventName,

      payload as Record<string, string> | undefined,
    ).catch((error) => {
      console.error(
        `[TriggerEngine] dispatchExternal("${eventName}") failed:`,

        error,
      );
    });
  }

  onAction(
    action: string,

    handler: TriggerHandler,
  ): TriggerUnsubscribe {
    if (!this.actionHandlers.has(action)) {
      this.actionHandlers.set(action, new Set());
    }

    this.actionHandlers.get(action)!.add(handler);

    return () => {
      this.actionHandlers.get(action)?.delete(handler);
    };
  }

  emitAction(action: string, payload?: unknown) {
    const handlers = this.actionHandlers.get(action);

    if (!handlers) return;

    for (const handler of handlers) {
      handler(payload);
    }
  }

  suppressEvent(event: string, ms: number) {
    this.suppressedEvents.add(event);

    setTimeout(() => {
      this.suppressedEvents.delete(event);
    }, ms);
  }

  async fireEvent(
    event: TriggerEvent,

    payload?: Record<string, string>,
  ) {
    if (!this.ready || this.executing) return;

    if (this.suppressedEvents.has(event)) return;

    // Notify global subscribers (e.g. overlay counters) before executing rules.

    for (const handler of this.globalEventHandlers) {
      try {
        handler(event, payload);
      } catch (error) {
        console.error("[TriggerEngine] global handler failed:", error);
      }
    }

    const rules = await this.getRules();

    this.executing = true;

    const rulesToRemove: string[] = [];

    for (const rule of rules) {
      if (rule.event !== event || !rule.enabled) continue;

      // If payload provided, match against eventConfig

      if (event === "event_chat") {
        // Chat rules use richer semantics (substring / regex / badge
        // filters) that the generic key-equality matcher can't express.
        if (!matchesChatEvent(rule.eventConfig ?? {}, payload)) continue;
      } else if (payload) {
        const matches = Object.entries(payload).every(
          ([k, v]) =>
            !rule.eventConfig[k] ||
            rule.eventConfig[k] === "__all__" ||
            rule.eventConfig[k] === v,
        );

        if (!matches) continue;
      }

      await this.executeAction(rule, payload);

      if (rule.once) {
        rulesToRemove.push(rule.id);
      }
    }

    this.executing = false;

    if (rulesToRemove.length > 0) {
      await this.removeRules(rulesToRemove);
    }
  }

  async getRules(): Promise<ITriggerRule[]> {
    try {
      const db = await openDatabase();

      const widgets = await db?.widgets.toArray();

      const triggersWidget = widgets?.find(
        (w) => w.type === WidgetsType.TRIGGERS,
      );

      if (!triggersWidget) return [];

      return (
        (triggersWidget as { configuration: { rules: ITriggerRule[] } })
          .configuration.rules ?? []
      );
    } catch {
      return [];
    }
  }

  private async removeRules(ruleIds: string[]) {
    try {
      const db = await openDatabase();

      const widgets = await db?.widgets.toArray();

      const triggersWidget = widgets?.find(
        (w) => w.type === WidgetsType.TRIGGERS,
      );

      if (!triggersWidget || !triggersWidget.id) return;

      const config = (
        triggersWidget as { configuration: { rules: ITriggerRule[] } }
      ).configuration;

      const idsToRemove = new Set(ruleIds);

      config.rules = config.rules.filter((r) => !idsToRemove.has(r.id));

      await db?.widgets.update(triggersWidget.id, {
        configuration: config,
      });

      await saveSnapshotDatabase();

      this.emitAction("rules_removed", { ruleIds });
    } catch (error) {
      console.error("[TriggerEngine] Failed to remove once-rules:", error);
    }
  }

  async executeAction(rule: ITriggerRule, eventPayload?: unknown) {
    const executor = actionRegistry.get(rule.action);

    if (!executor) return;

    this.emitAction("rule_executed", { ruleId: rule.id });

    try {
      await executor(this.Radio, rule.actionConfig ?? {}, {
        emitAction: this.emitAction.bind(this),

        suppressEvent: this.suppressEvent.bind(this),

        eventPayload,
      });
    } catch (error) {
      console.error(`[TriggerEngine] Action failed: ${rule.action}`, error);
    }
  }
}

export default TriggerEngine;
